import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BlockStack, Card, InlineStack, Text } from '@shopify/polaris';
import { apiFetch } from '../lib/api';
import { useTranslation } from '../i18n';

/**
 * Dashboard "Getting started" checklist — the core journey in four
 * steps with the next one highlighted:
 *
 *   1 Create a product → 2 Create ads → 3 Connect Meta & launch → 4 Build a website
 *
 * Presentation only: it reads the existing generations list
 * (`GET /api/generation`) and the Meta connection flag the dashboard
 * already fetches. Each card just navigates. Hidden once every step is
 * done, or while the state is still unknown (no flash of wrong advice).
 */
const DISMISS_KEY = 'autonoma_getting_started_dismissed';

export default function GettingStarted({ shop, metaConnected }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [gens, setGens] = useState(null); // null = loading
  const [dismissed, setDismissed] = useState(() => {
    try { return window.localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    if (!shop) return undefined;
    let cancelled = false;
    apiFetch('/api/generation?limit=100', { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (!cancelled) setGens(Array.isArray(data.generations) ? data.generations : []); })
      .catch(() => { if (!cancelled) setGens([]); });
    return () => { cancelled = true; };
  }, [shop]);

  if (dismissed || gens === null) return null;

  const has = (...types) => gens.some(g => types.includes(g.type));
  const steps = [
    { key: 'product', done: has('product_page', 'landing_page'), route: '/new-product' },
    { key: 'ads', done: has('ad_pack'), route: '/ads' },
    { key: 'meta', done: metaConnected === true, route: metaConnected === true ? '/ads' : '/settings' },
    { key: 'website', done: has('website'), route: '/website' },
  ];
  const doneCount = steps.filter(s => s.done).length;
  if (doneCount === steps.length) return null;
  const nextIndex = steps.findIndex(s => !s.done);

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, '1'); } catch { /* fine */ }
    setDismissed(true);
  };

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="050">
            <Text as="h2" variant="headingMd">{t('dashboard.gettingStarted.title')}</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {t('dashboard.gettingStarted.progress', { done: doneCount, total: steps.length })}
            </Text>
          </BlockStack>
          <button type="button" className="au-suggestion__dismiss" onClick={dismiss} data-testid="getting-started-dismiss">
            <span>{t('dashboard.gettingStarted.hide')}</span>
          </button>
        </InlineStack>
        <div className="au-checklist" data-testid="getting-started">
          {steps.map((s, i) => {
            const state = s.done ? 'done' : i === nextIndex ? 'next' : 'todo';
            return (
              <button
                key={s.key}
                type="button"
                className={`au-checklist__item au-checklist__item--${state}`}
                onClick={() => navigate(s.route)}
                data-testid={`getting-started-${s.key}`}
              >
                <span className="au-checklist__num" aria-hidden="true">{s.done ? '✓' : i + 1}</span>
                <span style={{ minWidth: 0 }}>
                  <span className="au-checklist__title">{t(`dashboard.gettingStarted.${s.key}.title`)}</span>
                  <span className="au-checklist__desc">{t(`dashboard.gettingStarted.${s.key}.desc`)}</span>
                  <span className="au-checklist__cta">
                    {s.done ? t('dashboard.gettingStarted.doneCta') : t(`dashboard.gettingStarted.${s.key}.cta`)} →
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </BlockStack>
    </Card>
  );
}
